import { CODING_STATUS_LABELS, codingFields, codingHistoryKey, parseCoding, type CodingState } from './meddraCoding';
import { localDateTimeLabel } from '@/features/catalog_case_review/shared/utils/caseReviewDateTime';

export interface SavedCodingSummary {
  status: string;
  version?: string;
  term: string;
  code?: string;
  preferredTerm?: string;
  soc?: string;
  hlgt?: string;
  hlt?: string;
  verbatim?: string;
  reason?: string;
}
export interface SavedCodingHistory {
  key: string;
  action: string;
  actor?: string;
  at?: string;
  before?: SavedCodingSummary;
  after?: SavedCodingSummary;
}
export interface SavedMeddraCoding extends SavedCodingSummary { history: SavedCodingHistory[] }

function summary(state: CodingState, fieldId: string): SavedCodingSummary {
  const fields = codingFields(fieldId);
  const path = state.term?.paths?.find(item => item.pathId === state.pathId);
  return {
    status: state.status ? CODING_STATUS_LABELS[state.status] ?? state.status : 'Not coded / unverified',
    version: state.version,
    term: state.raw?.[fields.term] || state.term?.name || 'No dictionary term selected',
    code: state.raw?.[fields.code] || state.term?.code || state.lltCode,
    preferredTerm: state.term?.pt,
    soc: path?.soc, hlgt: path?.hlgt, hlt: path?.hlt,
    verbatim: state.verbatim, reason: state.reason,
  };
}

/** Only human-readable coding properties leave this boundary; extraction/audit payloads do not. */
export function savedMeddraCoding(value: unknown, fieldId: string): SavedMeddraCoding {
  const state = parseCoding(value);
  return { ...summary(state, fieldId), history: (state.history ?? []).map(entry => ({
    key: codingHistoryKey(entry), action: entry.action, actor: entry.actor,
    at: entry.at ? localDateTimeLabel(entry.at) ?? entry.at : undefined,
    before: entry.before ? summary(entry.before, fieldId) : undefined,
    after: entry.after ? summary(entry.after, fieldId) : undefined,
  })) };
}

export const CODING_UNREADABLE = 'The MedDRA coding cannot be displayed';

/** A stored coding in one line, its term first: "Rhabdomyolysis · 10039020 · MedDRA 29.1". */
export function savedMeddraCodingText(value: unknown, fieldId: string): string {
  try {
    const coding = savedMeddraCoding(value, fieldId);
    return [coding.term, coding.code, coding.version && `MedDRA ${coding.version}`].filter(Boolean).join(' · ');
  } catch {
    // The stored value is not a coding record; saying so beats showing the raw text.
    return CODING_UNREADABLE;
  }
}
